import { getResult } from "../utils/resultProcess";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { BreadCrumb } from "../components/BreadCrumb";
import { Button } from "@mui/material";
import { useRef } from "react";
import html2canvas from "html2canvas-pro";
import { CircularProgress } from "@mui/material";

export const TeacherResult: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const navigate = useNavigate();
  const result = getResult();
  
  if (!result) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-600 mb-4">沒有找到批改結果</h2>
            <Button
              variant="contained"
              onClick={() => navigate("/teacher/correcting-records")}
            >
              返回批改記錄
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const correctingResult = JSON.parse(
    result?.correctingResult.replace(/```json\n|\n```/g, "") || "{}"
  );
  const breadCrumb = location.pathname;

  // 在截圖前，為中文文字添加正確的換行處理
  const fixCJKLineBreaks = (container: HTMLElement) => {
    const textNodes = container.querySelectorAll("p, span, div");
    textNodes.forEach((node) => {
      // 添加正確的文字換行控制
      (node as HTMLElement).style.wordBreak = "keep-all"; // 保持 CJK 文字完整性
    });
  };

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      // 1. 創建一個臨時容器用於截圖
      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.left = "-9999px";
      tempContainer.className = "bg-[#F7F7F7]";

      // 2. 只複製需要的內容
      const header = document.querySelector("header")?.cloneNode(true);
      const main = document.querySelector("main")?.cloneNode(true);

      if (header) tempContainer.appendChild(header);
      if (main) tempContainer.appendChild(main);

      // 3. 添加到文檔
      document.body.appendChild(tempContainer);

      // 4. 修復中文文字問題
      fixCJKLineBreaks(tempContainer);

      // 5. 進行截圖
      const canvas = await html2canvas(tempContainer, {
        backgroundColor: "#F7F7F7",
        scale: 2,
        useCORS: true,
      });

      // 6. 清理臨時容器
      document.body.removeChild(tempContainer);

      // 7. 下載圖片
      const link = document.createElement("a");
      link.download = `批改結果_${result.studentName || "學生"}_${new Date().toLocaleDateString()}.png`;
      link.href = canvas.toDataURL();
      link.click();

    } catch (error) {
      console.error("下載失敗:", error);
      alert("下載失敗，請重試");
    } finally {
      setIsLoading(false);
    }
  };

  const renderMarkdownTable = (content: string) => {
    if (!content) return null;
    
    // 簡單的 markdown 表格渲染
    const lines = content.split('\n').filter(line => line.trim());
    const tableLines = lines.filter(line => line.includes('|'));
    
    if (tableLines.length === 0) {
      return <div className="whitespace-pre-wrap">{content}</div>;
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300">
          <tbody>
            {tableLines.map((line, index) => {
              const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell);
              return (
                <tr key={index} className={index === 0 ? "bg-gray-100 font-bold" : ""}>
                  {cells.map((cell, cellIndex) => (
                    <td key={cellIndex} className="border border-gray-300 px-3 py-2">
                      {cell}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      <main className="flex-1 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <BreadCrumb breadCrumb={breadCrumb} />
          
          <div className="bg-white rounded-lg shadow-lg p-6 mt-6">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray-800">
                批改結果 - {result.studentName}
                {result.className && <span className="text-lg text-gray-600 ml-2">({result.className})</span>}
              </h1>
              <Button
                variant="contained"
                onClick={handleDownload}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
              >
                {isLoading ? "處理中..." : "下載結果"}
              </Button>
            </div>

            {/* 學生作文內容 */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-primary">學生作文</h2>
              <div className="border rounded-lg p-4 bg-gray-50">
                {result.type === "image" ? (
                  <img 
                    ref={imgRef}
                    src={result.input} 
                    alt="學生作文" 
                    className="max-w-full h-auto rounded"
                    onLoad={() => {}}
                  />
                ) : (
                  <div className="whitespace-pre-wrap text-gray-800">
                    {result.input}
                  </div>
                )}
              </div>
            </div>

            {/* 批改結果 */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-primary">AI 批改結果</h2>
              <div className="space-y-6">
                {Object.entries(correctingResult).map(([key, value]: [string, any]) => (
                  <div key={key} className="border rounded-lg p-4">
                    <h3 className="text-lg font-medium mb-3 text-gray-700">{key}</h3>
                    {typeof value === "object" ? (
                      <div className="space-y-2">
                        {Object.entries(value).map(([subKey, subValue]: [string, any]) => (
                          <div key={subKey} className="flex justify-between items-center">
                            <span className="font-medium">{subKey}:</span>
                            <span className="text-primary font-bold">{subValue}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      renderMarkdownTable(String(value))
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <Button
              variant="outlined"
              onClick={() => navigate("/teacher/correcting-records")}
            >
              返回批改記錄
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};